import React, { useEffect, useState, useRef } from "react";
import Papa from "papaparse";
import { getNamesForLevel, normalize, findBestMatch, fmtStateFull } from "../utils/helpers";
import { fetchR2CsvText } from "../utils/fetchR2Csv";

const TypeAheadPicker = ({
  areaType,
  allDates,
  latestByMetro,
  levelItems,
  minPopOnly,
  populationByCity,
  searchText,
  setSearchText,
  onCommit,
  feature, // when set to "feature1", use disambiguating renderer specific to Feature 1
  crumbsByName,
  onRequireAreaType, // optional callback to show "select level" warning in parent
}) => {
  const inputRef = useRef(null);
  const [forceClosed, setForceClosed] = useState(false);
  const hasAreaType = !!areaType;
  
  // keep the caret in the box after every update
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, [searchText, areaType]);

  const list = getNamesForLevel(areaType, allDates, latestByMetro, levelItems, minPopOnly, populationByCity);
  const isFeature1 = feature === "feature1";

  // Feature 1 only: load row-level data (with RegionID) from R2 Standard CSV so duplicates show distinctly
  const [levelRows, setLevelRows] = useState([]);
  useEffect(() => {
    if (!feature || feature !== "feature1") return;
    if (!areaType) return;
    const lower = String(areaType).toLowerCase();
    const r2Key = `public/data/Merged_No_Data/Standard/${lower}_5y_50k_standard_merged.csv`;
    let cancelled = false;
    (async () => {
      try {
        const text = await fetchR2CsvText(r2Key);
        // Robust CSV parsing to handle quoted commas and header variants
        Papa.parse(text, {
          header: true,
          dynamicTyping: false,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            if (cancelled) return;
            const rows = Array.isArray(result.data) ? result.data : [];
            const out = [];
            for (const r of rows) {
              const get = (k) => (r && r[k] != null ? String(r[k]).trim() : "");
          // Choose display Name with level-specific priority to avoid corrupted lists
          let nameValue = "";
          if (areaType === "Zipcode") {
            // For zip, prefer the actual zip code digits
            nameValue = get("Zipcode") || get("AreaName") || get("City") || get("Neighborhood") || get(areaType) || "";
          } else if (areaType === "Neighborhood") {
            nameValue = get("Neighborhood") || get("City") || get("CountyName") || get("AreaName") || get(areaType) || "";
          } else if (areaType === "City") {
            nameValue = get("City") || get("CountyName") || get("AreaName") || get(areaType) || "";
          } else if (areaType === "County") {
            nameValue = get("CountyName") || get("County") || get("AreaName") || get(areaType) || "";
          } else if (areaType === "State") {
            nameValue = get("State") || get("StateName") || get("AreaName") || get(areaType) || "";
          } else {
            // Metro or default
            nameValue = get("Metro") || get("AreaName") || get(areaType) || "";
          }

          const record = {
            RegionID: get("RegionID") || get("regionId") || get("region_id"),
            Name: nameValue,
            City: get("City"),
            County: get("CountyName") || get("County"),
            State: get("StateName") || get("State"),
          };
              if (!record.Name) continue;
              out.push(record);
            }
            setLevelRows(out);
          },
          error: () => {
            if (!cancelled) setLevelRows([]);
          },
        });
      } catch {
        if (!cancelled) setLevelRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [feature, areaType]);

  // --- Debounce: wait ~180ms after typing before filtering ---
  // const [debounced, setDebounced] = useState(searchText);
  // useEffect(() => {
  //   const t = setTimeout(() => setDebounced(searchText), 180);
  //   return () => clearTimeout(t);
  // }, [searchText]);

  // --- Pre-normalize names once for fast comparisons ---
  // const normList = useMemo(() => {
  //   return list.map((n) => ({ n, norm: normalize(n) }));
  // }, [list]);

  // --- Fast "prefix-first, then contains" suggestions (cap to 30) ---
  // const suggestions = useMemo(() => {
  //   const q = normalize(debounced);
  //   if (!q) return [];
  //   const cap = 30;

  //   // 1) prefix matches first
  //   const out = [];
  //   for (let i = 0; i < normList.length && out.length < cap; i++) {
  //     if (normList[i].norm.startsWith(q)) out.push(normList[i].n);
  //   }

  //   // 2) only if q has 3+ chars and we still have room, add substring matches
  //   if (q.length >= 3 && out.length < cap) {
  //     for (let i = 0; i < normList.length && out.length < cap; i++) {
  //       const it = normList[i];
  //       if (!it.norm.startsWith(q) && it.norm.includes(q)) out.push(it.n);
  //     }
  //   }
  //   return out;
  // }, [debounced, normList]);

  // Filtered list (after 1+ char)
  const q = normalize(searchText);
  const filtered = q.length >= 1 ? list.filter(n => normalize(n).includes(q)) : [];
  const limited = filtered.slice(0, 50);

  // Feature 1 row-based filtered list (keeps duplicates distinct)
  const normalizeZipDigits = (s) => String(s || "").replace(/\D+/g, "");
  const stripLeadingZeros = (s) => String(s || "").replace(/^0+/, "");
  const filteredRows = isFeature1 && q.length >= 1
    ? levelRows
        .filter((r) => {
          if (areaType === "Zipcode") {
            const nameDigits = normalizeZipDigits(r.Name);
            const queryDigits = normalizeZipDigits(searchText);
            if (!queryDigits) return false;
            const nameNoZero = stripLeadingZeros(nameDigits);
            const queryNoZero = stripLeadingZeros(queryDigits);
            // Match if either digits-without-zeros startsWith/includes the other
            return (
              nameDigits.startsWith(queryDigits) ||
              nameDigits.includes(queryDigits) ||
              queryDigits.startsWith(nameDigits) ||
              queryDigits.includes(nameDigits) ||
              nameNoZero.startsWith(queryNoZero) ||
              nameNoZero.includes(queryNoZero) ||
              queryNoZero.startsWith(nameNoZero) ||
              queryNoZero.includes(nameNoZero) ||
              nameNoZero === queryNoZero
            );
          }
          return normalize(r.Name).includes(q);
        })
        .slice(0, 50)
    : [];

  // Keyboard highlight
  const [hi, setHi] = useState(0);

  const commit = (value) => {
    const chosen = value != null ? value : findBestMatch(searchText, list);
    if (chosen) {
      // put the chosen full name into the input
      setSearchText(chosen);
      console.log("[COMMIT]", chosen);
      onCommit(chosen);
      // Reset highlight to close dropdown but keep the chosen name
      setHi(0);
      setForceClosed(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const commitRow = (row) => {
    if (!row) return;
    const name = row.Name;
    setSearchText(name);
    console.log("[COMMIT_ROW]", row);
    // Pass metadata for Feature 1 only (name visible; RegionID hidden but carried)
    onCommit({ name, regionId: row.RegionID || null, city: row.City || null, county: row.County || null, state: row.State || null });
    setHi(0);
    setForceClosed(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleChange = (e) => {
    const val = e.target.value;

    // If no level selected, block typing (keep input visually empty) and notify parent
    if (!hasAreaType) {
      if (val && typeof onRequireAreaType === "function") {
        onRequireAreaType();
      }
      // Do not update searchText when level is not selected
      setSearchText("");
      setHi(0);
      setForceClosed(false);
      return;
    }

    setSearchText(val);
    setHi(0);
    setForceClosed(false);
    inputRef.current?.focus();            // keep caret in the box

    const exact = list.find(n => normalize(n) === normalize(val));
    if (exact) commit(exact);
  };

  // Derived "open": show suggestions when typing but not when exact match is selected
  const exactMatch = list.find(n => normalize(n) === normalize(searchText));
  const open = (searchText?.trim()?.length || 0) >= 1 && (
    (isFeature1 ? filteredRows.length > 0 : limited.length > 0)
  ) && !exactMatch && !forceClosed;

  const handleKey = (e) => {
    // When no level is selected, prevent keyboard-driven commits and just notify parent
    if (!hasAreaType) {
      if ((e.key.length === 1 || e.key === "Enter" || e.key === "Backspace") && typeof onRequireAreaType === "function") {
        onRequireAreaType();
      }
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (isFeature1) {
        const pickRow = filteredRows[hi] || null;
        if (pickRow) commitRow(pickRow); else {
          const pick = findBestMatch(searchText, list);
          if (pick) commit(pick);
        }
      } else {
        const pick = limited[hi] || findBestMatch(searchText, list);
        if (pick) commit(pick);
      }
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi(i => Math.min(i + 1, Math.max(limited.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi(i => Math.max(i - 1, 0));
    }
  };

  

  // Build display parts for a given name using latestByMetro metadata (Feature 1 snapshot data)
  const buildItemDisplay = (name) => {
    const meta = latestByMetro?.[name] || {};
    const crumbs = crumbsByName?.[name] || {};
    const regionId = meta.RegionID || meta.regionId || meta.region_id || crumbs.RegionID || crumbs.regionId || crumbs.region_id || null;
    const stateRaw = meta.StateName || meta.State || meta.state || crumbs.StateName || crumbs.State || null;
    const county = meta.CountyName || meta.County || meta.county || crumbs.CountyName || crumbs.County || null;
    const city = meta.City || meta.city || crumbs.City || null;
    const metro = meta.Metro || meta.metro || crumbs.Metro || null;
    const zip = meta.Zipcode || meta.zip || crumbs.Zipcode || null;

    // If the visible name already contains a trailing state abbreviation (e.g., ", MI"), remove it
    let primaryName = name;
    const m = /,\s*([A-Z]{2})$/.exec(name);
    if (m) {
      primaryName = name.replace(/,\s*[A-Z]{2}$/, "");
    }

    let pathParts = [];
    switch (areaType) {
      case "Neighborhood":
        if (city) pathParts.push(city);
        if (county) pathParts.push(county);
        if (stateRaw) pathParts.push(fmtStateFull(stateRaw));
        break;
      case "City":
        if (county) pathParts.push(county);
        if (stateRaw) pathParts.push(fmtStateFull(stateRaw));
        break;
      case "County":
        if (stateRaw) pathParts.push(fmtStateFull(stateRaw));
        break;
      case "Zipcode":
        if (city) pathParts.push(city);
        if (stateRaw) pathParts.push(fmtStateFull(stateRaw));
        break;
      case "Metro":
        if (stateRaw) pathParts.push(fmtStateFull(stateRaw));
        break;
      case "State":
      default:
        break;
    }

    const secondary = pathParts.filter(Boolean).join(" • ");
    const metaKey = regionId || stateRaw || county || city || metro || zip || name;
    return { primary: primaryName, secondary, regionId, metaKey };
  };

  return (
    <div className="typeahead" style={{ position: "relative", display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
      <div style={{ position: "relative", flex: "1 1 360px" }}>
        <input
          ref={inputRef}
          type="text"
          className="typeahead-input"
          value={searchText}
          onChange={handleChange}
          onInput={handleChange}
          onKeyDown={handleKey}
          placeholder={areaType ? `Type ${areaType.toLowerCase()} name…` : "Type area name…"}
          autoComplete="off"
          style={{ width: "100%" }}
        />
        {open && (
          <ul
            className="typeahead-menu"
            role="listbox"
            onMouseDown={(e) => e.preventDefault()}   // don't steal focus from input
          >
            {(isFeature1 ? filteredRows : limited).map((item, i) => {
              if (!isFeature1) {
                const n = item;
                return (
                  <li
                    key={n}
                    role="option"
                    aria-selected={i === hi}
                    className={i === hi ? "active" : ""}
                    onMouseDown={(e) => { e.preventDefault(); setSearchText(n); commit(n); }}
                  >
                    {n}
                  </li>
                );
              }

              const row = item;
              const n = row.Name;
              const disp = buildItemDisplay(n);
              // Use row City/County/State to guarantee per-row context
              const rowSecondaryParts = [];
              if (row.City) rowSecondaryParts.push(row.City);
              if (row.County) rowSecondaryParts.push(row.County);
              if (row.State) rowSecondaryParts.push(fmtStateFull(row.State));
              const secondary = rowSecondaryParts.join(" • ") || disp.secondary;
              return (
                <li
                  key={`${row.RegionID || n}|${i}`}
                  role="option"
                  aria-selected={i === hi}
                  className={i === hi ? "active" : ""}
                  onMouseDown={(e) => { e.preventDefault(); commitRow(row); }}
                  style={{ display: "flex", flexDirection: "column", padding: "6px 10px" }}
                >
                  <div style={{ fontWeight: 600 }}>{disp.primary}</div>
                  {secondary && (
                    <div style={{ fontSize: 12, color: "#555" }}>{secondary}</div>
                  )}
                  {/* RegionID intentionally hidden in dropdown presentation */}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TypeAheadPicker;
